import React, { useState } from 'react';
import { WorkoutExercise, ExerciseDefinition } from '../../../types';
import { useTranslation } from '../../../i18n/provider';
import ExerciseInfo from './ExerciseInfo';
import ConfirmationModal from '../../common/ConfirmationModal';
import styles from './TodayWorkout.module.css';

interface TodayWorkoutProps {
  exercises: WorkoutExercise[];
  onAddExercise: (exercise: ExerciseDefinition) => void;
  onDeleteExercise: (id: string) => void;
  onEditExercise: (exercise: WorkoutExercise) => void;
  onEndWorkout: (exercises: WorkoutExercise[]) => void;
  onOpenTemplates?: () => void;
  onNavigateToAddExercise?: () => void;
  onNavigateToExercise?: (exerciseId: string, templateId?: string) => void;
  onDateChange?: (date: Date) => void;
  selectedDate: Date;
}

const TodayWorkout: React.FC<TodayWorkoutProps> = ({ exercises, onAddExercise, onDeleteExercise, onEditExercise, onEndWorkout, onOpenTemplates, onNavigateToAddExercise, onNavigateToExercise, onDateChange, selectedDate }) => {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState<WorkoutExercise | null>(null);

  
  // Check if selected date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Handle date change
  const handleDateChange = (newDate: Date) => {
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  // Get min date (2 months ago)
  const getMinDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 2);
    return date;
  };

  // Get max date (today)
  const getMaxDate = () => {
    return new Date();
  };

  const handleDeleteClick = (exercise: WorkoutExercise) => {
    // Check if exercise has completed sets
    if (exercise.completedSets > 0) {
      setExerciseToDelete(exercise);
      setShowDeleteConfirm(true);
    } else {
      // Delete immediately if no completed sets
      onDeleteExercise(exercise.id);
    }
  };

  const handleConfirmDelete = () => {
    if (exerciseToDelete) {
      onDeleteExercise(exerciseToDelete.id);
      setExerciseToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelDelete = () => {
    setExerciseToDelete(null);
    setShowDeleteConfirm(false);
  };
  const renderExerciseItem = (exercise: WorkoutExercise) => {
    // Use setsData.length for completed sets when available, otherwise fall back to completedSets
    const actualCompletedSets = exercise.setsData ? exercise.setsData.length : exercise.completedSets;
    
    // Calculate progress percentage
    const progressPercent = exercise.sets > 0 ? (actualCompletedSets / exercise.sets) * 100 : 0;
    
    // Determine battery color based on progress
    let batteryColor = '#d32f2f'; // red for 0%
    if (progressPercent === 100) {
      batteryColor = '#2e7d32'; // green for 100%
    } else if (progressPercent > 0) {
      batteryColor = '#ff9800'; // orange/yellow for in progress
    }

    return (
      <div 
        key={exercise.id} 
        className={styles.exerciseItem}
        style={{ position: 'relative', cursor: 'pointer' }}
        onClick={() => onEditExercise(exercise)}
      >
        <div className={styles.exerciseState}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Battery outline */}
            <rect x="2" y="6" width="18" height="12" rx="2" stroke={batteryColor} strokeWidth="2" fill="none"/>
            {/* Battery tip */}
            <rect x="20" y="9" width="2" height="6" fill={batteryColor}/>
            {/* Battery fill based on progress */}
            {progressPercent > 0 && (
              <rect 
                x="4" 
                y="8" 
                width={`${(progressPercent / 100) * 14}`} 
                height="8" 
                fill={batteryColor}
                opacity="0.9"
              />
            )}
          </svg>
        </div>
        <ExerciseInfo exercise={exercise} onNavigateToExercise={onNavigateToExercise} />
        <button
          className={styles.deleteButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(exercise);
          }}
          title="Delete exercise"
          aria-label="Delete exercise"
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            <line x1="10" y1="11" x2="10" y2="17"></line>
            <line x1="14" y1="11" x2="14" y2="17"></line>
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="workout-content">
      <div className={styles.contentWrapper}>
        <div className={`content-card ${styles.contentCard}`}>
          <div className={styles.titleSection}>
            <h1 className="content-title">
              {isToday(selectedDate) ? t('workout.today.title') : t('workout.today.fallbackTitle')}
            </h1>
            <div className={styles.dateSection}>
              <div className={styles.datePicker}>
                  <input
                    type="date"
                    min={getMinDate().toISOString().split('T')[0]}
                    max={getMaxDate().toISOString().split('T')[0]}
                    value={selectedDate.toISOString().split('T')[0]}
                    onChange={(e) => handleDateChange(new Date(e.target.value))}
                    className={styles.dateInput}
                  />
                </div>
            </div>
          </div>
          <div className={styles.exercisesList}>
            {exercises.length > 0 ? (
              exercises.map(renderExerciseItem)
            ) : (
              <div style={{ width: '100%' }}>
                <div style={{ opacity: 0.8, padding: '12px 0' }}>{t('workout.today.empty')}</div>
                <div style={{ marginTop: 12 }}>
                  <button
                    className="button button--secondary"
                    style={{ width: '100%' }}
                    onClick={() => onOpenTemplates && onOpenTemplates()}
                  >
                    {t('workout.today.templatesButton')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="action-buttons">
        <button className="button button--secondary" onClick={onNavigateToAddExercise}>
          <span>+</span>
          <span>{t('workout.today.addExercise')}</span>
        </button>
        {exercises.length > 0 && exercises.some(ex => (ex.sets ?? 0) > 0) && (
          <button className="button button--danger" onClick={() => onEndWorkout(exercises)}>
            <span>▶</span>
            <span>{t('workout.today.endWorkout')}</span>
          </button>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title={t('workout.today.deleteTitle')}
        message={t('workout.today.deleteMessage', {
          name: exerciseToDelete?.name || '',
          count: exerciseToDelete?.completedSets || 0,
        })}
        confirmText={t('workout.today.delete')}
        cancelText={t('workout.today.cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isDangerous={true}
      />
    </div>
  );
};

export default TodayWorkout;
